import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {   Subject } from 'rxjs';

import { OnlineStatusService } from 'src/app/online-status.service';

import { defaultJobIcon, jobStageIcons, PRESET_FILTERS } from '../../global.constants';
import { DISPATCH_PERMISSIONS, FRONTEND_PERMISSIONS } from '../../permissions.constants';
import { BrandingService } from '../../services/branding.service';
import { PermissionService } from '../../services/permission.service';
import { JobParams } from '../../shared/query-params.service';

export interface SidebarItem {
    id?: string;
    label: string;
    sublabel?: string;
    icon: string;
    routerLink?: string[];
    queryParams?: any;

    alwaysShowItems?: boolean;

    tooltip?: string;

    hiddenByDefault?: boolean;
    permissions?: string;
    items?: SidebarItem[];
    hidden?: boolean;
    disabled?: boolean;
    hiddenIfZoneType?: string[];
    command?: (res: { originalEvent: Event; item: SidebarItem }) => void;

    additionalAction?: {
        icon?: string;
        tooltip?: string;
        routerLink?: string[];
        queryParams?: any;
        hidden?: boolean;
        disabled?: boolean;
        command?: (res: { originalEvent: Event; item: SidebarItem }) => void;

        // hiddenByDefault?: boolean;
        // permissions?: string;
    };
}

@Injectable()
export class MenuService {
    isOnline: boolean;

    get menuSource$() { return this.menuSource.asObservable(); }
    get resetSource$() { return this.resetSource.asObservable(); }

    sidebar: SidebarItem[];

    private menuSource = new Subject<string>();
    private resetSource = new Subject<void>();

    // private scheduleDate$ = this.store.select(scheduleFeature.selectFormattedDate);
    private scheduleDate: string;
    private isDispatchEnabled = false;
    private canViewDispatch = false;
    /** The Schedule child tabs gated like the page's own tab list (Ray:
     *  "all our tabs are also in the sidebar") — the dispatch MAP tab and
     *  the Ops Readiness dashboard. */
    private canViewMap = false;
    private canViewOpsDashboard = false;
    public isJobCreationToolEnabled = false;
    public isQuickAccessEnabled = false;

    get zoneId() {
        return this.branding.currentZone().value?.id;
    }

    get isJobCreationEnabled() {
        return this.isJobCreationToolEnabled;
    }

    constructor(
        private branding: BrandingService,
        public router: Router,
        private onlineStatusService: OnlineStatusService,
        private store: Store,
        private permissionService: PermissionService,
    ) {
        this.onlineStatusService.isOnline$.subscribe(status => {
            this.isOnline = status;
            this.setSidebar();
        });

        // this.scheduleDate$.pipe(filter(Boolean), distinctUntilChanged()).subscribe(date => {
        //     this.scheduleDate = date;
        //     this.setSidebar();
        // });

        this.branding.watchFeature('dispatch').subscribe((enabled) => {
            this.isDispatchEnabled = enabled;
            this.setSidebar();
        });

        this.permissionService.watchPermission(DISPATCH_PERMISSIONS.viewDispatch).subscribe((hasPermission) => {
            this.canViewDispatch = hasPermission;
            this.setSidebar();
        });

        // The dispatch MAP tab rides the same dispatch feature flag as the
        // page's tab list, gated by its own permission (viewMap).
        this.permissionService.watchPermission(DISPATCH_PERMISSIONS.viewMap).subscribe((hasPermission) => {
            this.canViewMap = hasPermission;
            this.setSidebar();
        });

        // Ops Readiness is independent of the dispatch feature; its own
        // dashboard permission alone decides the sidebar entry (mirrors
        // SchedulePageComponent's tabPermissions gate).
        this.permissionService.watchPermission(FRONTEND_PERMISSIONS.dashboardOpsReadiness).subscribe((hasPermission) => {
            this.canViewOpsDashboard = hasPermission;
            this.setSidebar();
        });

        this.branding.watchFeature('new-job-creation-tool').subscribe((enabled) => {
            this.isJobCreationToolEnabled = enabled;
            this.setSidebar();
        });

        this.branding.watchFeature('quick-access').subscribe((enabled) => {
            this.isQuickAccessEnabled = enabled;
            this.setSidebar();
        });
    }

    onMenuStateChange(key: string) {
        this.menuSource.next(key);
    }

    reset() {
        this.resetSource.next();
    }

    getSidebarItemByID(id: string): SidebarItem | undefined {
        return this.sidebar?.find((i) => i.id === id);
    }

    getJobParamsByName(name: string): JobParams {
        return PRESET_FILTERS.jobs.find((f) => f.name.toLowerCase() === name.toLowerCase())?.filter;
    }

    setSidebar() {
        this.sidebar = [
            {
                label: 'Home',
                icon: 'pi pi-home',
                routerLink: ['/home'],
                disabled: !this.isOnline,
            },
            {
                label: 'Schedule',
                icon: 'pi pi-calendar-times',
                routerLink: ['/schedule'],
                permissions: FRONTEND_PERMISSIONS.schedule,
                disabled: !this.isOnline,
                items: [
                    {
                        label: 'Day',
                        disabled: !this.isOnline,
                        icon: 'pi pi-calendar-clock',
                        routerLink: ['/schedule/', 'day'],
                        queryParams: {
                            date: this.scheduleDate,
                            zone: this.zoneId,
                        },
                    },
                    {
                        label: 'Week',
                        disabled: !this.isOnline,
                        icon: 'pi pi-calendar',
                        routerLink: ['/schedule/', 'week'],
                        queryParams: {
                            date: this.scheduleDate,
                            zone: this.zoneId,
                        },

                    },
                    {
                        label: 'Month',
                        disabled: !this.isOnline,
                        icon: 'pi pi-calendar-minus',
                        routerLink: ['/schedule/month'],
                        queryParams: {
                            date: this.scheduleDate,
                            zone: this.zoneId,
                        },

                    },
                    {
                        label: 'Dispatch',
                        disabled: !this.isOnline,
                        hidden: !this.isDispatchEnabled || !this.canViewDispatch,
                        icon: 'pi pi-truck',
                        routerLink: ['/schedule/dispatch'],
                        queryParams: {
                            date: this.scheduleDate,
                            zone: this.zoneId,
                        },
                    },
                    {
                        label: 'Map',
                        disabled: !this.isOnline,
                        hidden: !this.isDispatchEnabled || !this.canViewMap,
                        icon: 'pi pi-map',
                        routerLink: ['/schedule/map'],
                        queryParams: {
                            date: this.scheduleDate,
                            zone: this.zoneId,
                        },
                    },
                    {
                        label: 'Ops Readiness',
                        disabled: !this.isOnline,
                        hidden: !this.canViewOpsDashboard,
                        icon: 'pi pi-gauge',
                        routerLink: ['/schedule/ops-readiness'],
                        queryParams: {
                            date: this.scheduleDate,
                            zone: this.zoneId,
                        },
                    }
                ]
            },
            {
                label: 'Jobs',
                icon: defaultJobIcon,
                disabled: !this.isOnline,
                routerLink: ['/jobs'],
                queryParams: {},
                items: [
                    {
                        label: 'Leads',
                        disabled: !this.isOnline,
                        icon: jobStageIcons.lead,
                        routerLink: ['/jobs', 'open-leads'],
                    },
                    {
                        label: 'Estimates',
                        icon: jobStageIcons.estimate,
                        disabled: !this.isOnline,
                        routerLink: ['/jobs', 'open-estimates'],
                    },
                    {
                        label: 'Bookings',
                        icon: jobStageIcons.booking,
                        disabled: !this.isOnline,
                        routerLink: ['/jobs', 'open-bookings'],
                    },
                    {
                        label:
                            'Invoices',
                        icon: jobStageIcons.invoice,
                        disabled: !this.isOnline,
                        routerLink: ['/jobs', 'open-invoices'],
                    },
                    {
                        label: 'Pending',
                        icon: 'pi pi-exclamation-circle',
                        disabled: !this.isOnline,
                        routerLink: ['/jobs', 'pending'],
                    },
                    {
                        label: 'Closed',
                        icon: 'pi pi-circle-fill',
                        disabled: !this.isOnline,
                        routerLink: ['/jobs', 'closed'],
                    },
                ],
                permissions: FRONTEND_PERMISSIONS.jobs
            },
            // {label: 'Leads', icon: 'pi pi-folder-open', routerLink: ['/leads']},
            {
                label: 'People',
                items: [
                    {
                        label: 'Customers',
                        icon: 'pi pi-shopping-cart',
                        disabled: !this.isOnline,
                        routerLink: ['/people/customers']
                    },
                    {
                        label: 'Employees',
                        icon: 'pi pi-id-card',
                        disabled: !this.isOnline,
                        routerLink: ['/people/employees']
                    },
                    {
                        label: 'Crew',
                        icon: 'pi pi-briefcase',
                        disabled: !this.isOnline,
                        routerLink: ['/people/crew']
                    },
                ],
                icon: 'pi pi-users',
                disabled: !this.isOnline,
                routerLink: ['/people'],
                permissions: FRONTEND_PERMISSIONS.users
            },
            {
                label: 'Reports',
                disabled: !this.isOnline,
                icon: 'pi pi-chart-line',
                routerLink: ['/reports'],
                permissions: FRONTEND_PERMISSIONS.reports
            },
            {
                label: 'Business',
                disabled: !this.isOnline,
                icon: 'pi pi-table',
                routerLink: ['/business'],
                permissions: FRONTEND_PERMISSIONS.franchise
            },
        ].filter(Boolean);
    }

}
